import { FC, useState, useEffect, useMemo } from 'react'
import { Modal } from '../Atoms/Modal'
import {
  XMarkIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  PrinterIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import {
  DeliveryEnum,
  PrintShipmenttEnum,
  ShipmentContentInvoiceInterfaces,
  ShipmentInterface,
  ShipmentStatusEnum,
  ShipmentTracking,
} from '../../interfaces'
import {
  buttonClassname,
  LinkText,
  PrimaryButton,
  SecondaryButton,
} from '../Atoms/Buttons'
import moment from 'moment'
import classNames from 'classnames'
import { TimelineItem } from '../Atoms/TimelineItem'
import Dropdown from '../Atoms/Dropdown'
import {
  previewFileDO,
  getShipment,
  getShipmentTracking,
  getShipmentContentInvoice,
  updateShipmentStatus,
} from '../../services'
import { mapServiceToEnum } from '../../utils/mappers'
import { ShipmentService } from '../../interfaces/Services/ServicesEnum'
import { useAppSelector } from '../../store/hooks'
import LoadingIcon from '../LoadingIcon'
import ModalLoading from '../Atoms/ModalLoading'
import { ConfirmModalStatus } from '../Preshipment/ModalSuccess'
import logo from '../../assets/images/TealcaLogo.png'

import { ShipmentStatus } from '../../modules/ShipmentV2/filterOptions'

interface ShipmentDetailModalProps {
  open: boolean
  // shipment: StoreShipmentInterface
  shipmentNumber: string
  onPrint: () => void
  //tracking: ShipmentTracking[]
  //loadingTracking: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  setTypePrint: React.Dispatch<React.SetStateAction<number>>
  onSuccess?: () => void
}

export const ShipmentDetailModal: FC<ShipmentDetailModalProps> = ({
  open,
  //shipment,
  onPrint,
  setOpen,
  shipmentNumber,
  setTypePrint,
  onSuccess,
  //tracking,
  //loadingTracking,
}) => {
  const user = useAppSelector((state) => state.user)!
  const [openAnulation, setOpenAnnulation] = useState(false)
  const [loadingModal, setLoadingModal] = useState<boolean>(false)
  const [loadingStatus, setLoadingStatus] = useState<ConfirmModalStatus>(
    ConfirmModalStatus.PROCESSING
  )
  const [loadingTitle, setLoadingTitle] = useState<string>('')

  const [shipment, setShipment] = useState<ShipmentInterface>(
    {} as ShipmentInterface
  )
  const [loadingShipment, setLoadingShipment] = useState<boolean>(false)

  const [tracking, setTracking] = useState<ShipmentTracking[]>([])
  const [loadingTracking, setLoadingTracking] = useState(false)
  const [annulmentObservation, setAnnulmentObservation] = useState('')
  const taxIdentificationTypes = useAppSelector(
    (state) => state.inmutable.taxIdentificationTypes
  )

  const getInvoiceDeclarated = async (
    shipment: ShipmentInterface
  ): Promise<ShipmentContentInvoiceInterfaces[]> => {
    const shipmentHeaderId = shipment?.id
    if (!shipmentHeaderId) return []

    const search = await getShipmentContentInvoice(shipmentHeaderId.toString())

    if (search.didError || !search.model) return []

    return Array.isArray(search.model) ? search.model : [search.model]
  }

  const renderTitle = (title: string) => {
    return title.split('\n').map((line, i) => (
      <span key={i}>
        {line}
        {i < title.split('\n').length - 1 && <br />}
      </span>
    ))
  }

  const isModifiable = useMemo(() => {
    return (
      !!shipment &&
      shipment.status === ShipmentStatusEnum.DRAFT &&
      (user.user?.profileID == 201 || user.user?.profileID === 209)
    )
  }, [shipment, user.user?.login])

  const getTracking = async (shipment: ShipmentInterface) => {
    const service = shipment?.service
    const buSource = shipment?.buConsignee?.buCode
    const shipmentNumber = shipment?.number
    setTracking([])
    if (!shipmentNumber || !service || !buSource) return
    const serviceEnum = mapServiceToEnum(service) ?? ShipmentService.STANDARD
    setLoadingTracking(true)
    const tracking = await getShipmentTracking(
      shipmentNumber?.toString(),
      serviceEnum,
      buSource
    ).finally(() => {
      setLoadingTracking(false)
    })

    if (!tracking) return

    setTracking(
      tracking.sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime()
      })
    )
  }

  const fetchShipment = async () => {
    if (shipmentNumber) {
      setLoadingShipment(true)
      try {
        const shipmentData = await getShipment(shipmentNumber)
        if (shipmentData) {
          shipmentData.shipper.abreviationName =
            taxIdentificationTypes.find(
              (t) =>
                t.taxIdentificationTypeId ===
                shipmentData?.shipper?.taxIdentificationTypeID
            )?.abbreviationName || ''
          shipmentData.consignee.abbreviationName =
            taxIdentificationTypes.find(
              (t) =>
                t.taxIdentificationTypeId ===
                shipmentData?.consignee?.taxIdentificationTypeID
            )?.abbreviationName || ''
          setShipment(shipmentData)
          setAnnulmentObservation(shipmentData.observations || '')
          shipmentData.ShipmentContentInvoiceList =
            await getInvoiceDeclarated(shipmentData)
          getTracking(shipmentData)
        }
      } catch (error) {
        console.error('Error fetching shipment:', error)
      } finally {
        setLoadingShipment(false)
      }
    }
  }

  useEffect(() => {
    if (open) fetchShipment()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentNumber, open])

  const handleAnulation = async () => {
    if (!shipment?.id) return
    setOpenAnnulation(false)
    setLoadingTitle('Anulando guía...\nPor favor espere')
    setLoadingStatus(ConfirmModalStatus.PROCESSING)
    setLoadingModal(true)

    const response = await updateShipmentStatus(
      ShipmentStatus.ANNULLED,
      shipment?.id,
      shipment?.shipmentNumber ?? '',
      user.user ?? undefined,
      annulmentObservation
    )

    if (response.didError) {
      setLoadingStatus(ConfirmModalStatus.ERROR)
      setLoadingTitle(
        `Error al anular la guía\n${
          response.errorMessage ?? 'No se pudo anular la guía.'
        }`
      )
    } else {
      setLoadingStatus(ConfirmModalStatus.SUCCESS)
      setLoadingTitle('Guía anulada correctamente.')
      if (onSuccess) onSuccess()
      await fetchShipment()
    }
  }

  return (
    <>
      <Modal
        open={open}
        setOpen={setOpen}
        staticBackdrop={true}
        className="flex flex-1 mx-4 my-8"
        style={{ maxWidth: '55rem' }}
      >
        <div className="flex flex-1 flex-col">
          {/* Header */}
          <div className="flex flex-1 items-center justify-between">
            <h2 className="text-xl font-bold">
              <span className="text-gray-700 hidden sm:inline">
                Número de Guía :{' '}
              </span>

              <>
                <span className="text-main-500">
                  {shipmentNumber}{' '}
                  {/* {!loadingShipment && (
                    <>
                      ({shipment.status?.toUpperCase()}) |{' '}
                      {shipment.paymentMode?.toUpperCase()}{' '}
                    </>
                  )} */}
                </span>
              </>
            </h2>

            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-main-500"
                onClick={() => setOpen(false)}
              >
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>

          <hr className="border-gray-300 my-3" />

          {loadingShipment ? (
            <div className="flex flex-col items-center">
              <div className="relative">
                <LoadingIcon size="8rem" />
                <img
                  src={logo}
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-15 h-16 animate-pulse"
                  alt="Tealca logo"
                />
              </div>
              <p className="text-center font-bold break-words px-2 mt-4 animate-pulse whitespace-pre-line">
                {' '}
                {renderTitle(
                  'Cargando detalles de la guía...\nPor favor espere'
                )}
              </p>
            </div>
          ) : shipment?.shipmentNumber ? (
            <>
              {/* Shipment Data */}
              <div className="flex flex-1 flex-col text-gray-800 leading-0 text-xs sm:px-8 gap-1 sm:gap-0">
                <div className="flex flex-1 flex-col">
                  <div className="flex flex-1 items-center gap-2">
                    <h3 className="text-md text-main-500 font-bold">
                      Detalles de la guía
                    </h3>
                  </div>

                  <hr className="border-1.5 border-main-500 mb-3 mt-1" />
                </div>

                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Estatus
                  </span>
                  <span className="flex-1 sm:flex-3">{shipment.status}</span>
                </div>
                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Modalidad de pago:
                  </span>
                  <span className="flex-1 sm:flex-3">
                    {shipment.paymentMode?.toUpperCase()}{' '}
                  </span>
                </div>

                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Fecha de creación
                  </span>

                  <span className="flex-1 sm:flex-3">
                    {!!shipment.createdAt &&
                      moment(shipment.createdAt).format('DD/MM/YYYY')}
                  </span>
                </div>

                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Nº de piezas:
                  </span>

                  <span className="flex-1 sm:flex-3">
                    {shipment.pieces?.length}
                  </span>
                </div>

                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Peso por facturar:
                  </span>

                  <span className="flex-1 sm:flex-3">
                    {shipment.totalChargedWeight?.toFixed(2)} Kg
                  </span>
                </div>
                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Peso balanza:
                  </span>

                  <span className="flex-1 sm:flex-3">
                    {shipment.totalPhysicalWeight?.toFixed(2)} Kg
                  </span>
                </div>
                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Peso volumétrico:
                  </span>

                  <span className="flex-1 sm:flex-3">
                    {shipment.totalDimensionalWeight?.toFixed(2)} Kg
                  </span>
                </div>

                {shipment.shipmentHeaderReference !== null && (
                  <div className="flex flex-1 gap-6">
                    <span className="flex flex-1 flex-col text-right font-semibold truncate">
                      Guía aliada
                    </span>

                    <span className="flex-1 sm:flex-3">
                      {shipment.shipmentHeaderReference}
                    </span>
                  </div>
                )}

                {shipment.externalManifest !== undefined &&
                  shipment.externalManifest?.length > 0 && (
                    <div className="flex flex-1 gap-6">
                      <span className="flex flex-1 flex-col text-right font-semibold truncate">
                        Manifiesto Externo
                      </span>

                      <span className="flex-1 sm:flex-3">
                        {shipment.externalManifest}
                      </span>
                    </div>
                  )}

                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    ¿Viaja asegurada?
                  </span>
                  <span className="flex-1 sm:flex-3">
                    {shipment.isSafeKeeping === true ? 'Si' : 'No'}
                  </span>
                </div>

                {/* Documentos Valor Declarado */}
                {shipment.ShipmentContentInvoiceList &&
                  shipment.ShipmentContentInvoiceList.length > 0 && (
                    <>
                      <div className="flex flex-1 gap-6">
                        <span className="flex flex-1 flex-col text-right font-semibold truncate">
                          Valor Declarado:
                        </span>

                        <span className="flex-1 sm:flex-3">
                          {shipment.declaratedValue?.toFixed(2) ?? 0}
                        </span>
                      </div>
                      {shipment.declaratedValueInvoceNum && (
                        <>
                          <div className="flex flex-1 gap-6">
                            <span className="flex flex-1 flex-col text-right font-semibold truncate">
                              N° de factura del valor declarado
                            </span>

                            <span className="flex-1 sm:flex-3">
                              {shipment.declaratedValueInvoceNum}
                            </span>
                          </div>
                        </>
                      )}
                      {shipment.ShipmentContentInvoiceList.length > 0 && (
                        <>
                          <div className="flex flex-1 gap-6">
                            <span className="flex flex-1 flex-col text-right font-semibold truncate">
                              Documentos valor declarado
                            </span>

                            <span className="flex-1 sm:flex-3">
                              {shipment.ShipmentContentInvoiceList.map(
                                (invoice, index) => (
                                  <button
                                    key={invoice.attachmentID}
                                    onClick={() =>
                                      previewFileDO(invoice.attachmentID)
                                    }
                                    className="px-3 py-1 bg-gray-100 hover:bg-main-500 hover:text-white rounded-md text-sm font-medium ml-1"
                                  >
                                    Ver Factura {index + 1}
                                  </button>
                                )
                              )}
                            </span>
                          </div>
                        </>
                      )}
                    </>
                  )}
                {shipment.ShipmentContentInvoiceList &&
                  shipment.ShipmentContentInvoiceList.length === 0 && (
                    <>
                      <div className="flex flex-1 gap-6">
                        <span className="flex flex-1 flex-col text-right font-semibold truncate">
                          Valor declarado
                        </span>

                        <span className="flex-1 sm:flex-3">
                          {shipment.declaratedValue?.toFixed(2) ?? 0}
                        </span>
                      </div>
                      {shipment.declaratedValueInvoceNum && (
                        <>
                          <div className="flex flex-1 gap-6">
                            <span className="flex flex-1 flex-col text-right font-semibold ">
                              N° de factura del valor declarado
                            </span>

                            <span className="flex-1 sm:flex-3">
                              {shipment.declaratedValueInvoceNum}
                            </span>
                          </div>
                        </>
                      )}
                    </>
                  )}

                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Observaciones
                  </span>

                  <span className="flex-1 sm:flex-3">
                    {shipment.observations}
                  </span>
                </div>

                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Creada por
                  </span>

                  <span className="flex-1 sm:flex-3">
                    {shipment.creationUser}
                  </span>
                </div>
              </div>

              {/* Shipper Data */}
              <div className="flex flex-1 flex-col text-gray-800 leading-0 text-xs sm:px-8 mt-6 gap-1 sm:gap-0">
                <div className="flex flex-1 flex-col">
                  <div className="flex flex-1 items-center gap-2">
                    <h3 className="text-md text-main-500 font-bold">
                      Detalles del remitente
                    </h3>
                  </div>

                  <hr className="border-1.5 border-main-500 mb-3 mt-1" />
                </div>

                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Nombre y apellido
                  </span>

                  <span className="flex-1 sm:flex-3">
                    {shipment.shipper.accountFullName}
                  </span>
                </div>

                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Documento
                  </span>
                  <span className="flex-1 sm:flex-3">
                    {shipment.shipper.abreviationName}
                    {shipment.shipper.identificationNumber}
                  </span>
                </div>

                {/* <div className="flex flex-1 gap-6">
                <span className="flex flex-1 flex-col text-right font-semibold truncate">
                  Persona de Contacto
                </span>
                <span className="flex-1 sm:flex-3">
                  {shipment.shipper.contactPerson}
                </span>
              </div> */}

                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Tienda origen
                  </span>

                  <span className="flex-1 sm:flex-3">
                    {shipment.buSource?.buCode} - {shipment.buSource?.buArea}
                  </span>
                </div>
                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Ciudad origen
                  </span>

                  <span className="flex-1 sm:flex-3">
                    {shipment.buSource?.location?.name}
                  </span>
                </div>
              </div>

              {/* Consignee Data */}
              <div className="flex flex-1 flex-col text-gray-800 leading-0 text-xs sm:px-8 mt-6 gap-1 sm:gap-0">
                <div className="flex flex-1 flex-col">
                  <div className="flex flex-1 items-center gap-2">
                    <h3 className="text-md text-main-500 font-bold">
                      Detalles del destinatario
                    </h3>
                  </div>

                  <hr className="border-1.5 border-main-500 mb-3 mt-1" />
                </div>
                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Nombre y apellido
                  </span>

                  <span className="flex-1 sm:flex-3">
                    {shipment.consignee.accountFullName}
                  </span>
                </div>
                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Documento
                  </span>

                  <span className="flex-1 sm:flex-3">
                    {shipment.consignee.abbreviationName}
                    {shipment.consignee.identificationNumber}
                  </span>
                </div>

                {/* <div className="flex flex-1 gap-6">
                <span className="flex flex-1 flex-col text-right font-semibold truncate">
                  Pais
                </span>
                <span className="flex-1 sm:flex-3">
                  {shipment.consigneeAddress?.}
                </span>
              </div> */}

                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Correo
                  </span>
                  <span className="flex-1 sm:flex-3">
                    {shipment.consignee.listAccountEmail[0]?.email}
                  </span>
                </div>
                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Teléfono
                  </span>
                  <span className="flex-1 sm:flex-3">
                    {shipment.consignee.listAccountPhone[0]?.phoneNumber}
                  </span>
                </div>
                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Entrega
                  </span>
                  <span className="flex-1 sm:flex-3">
                    {shipment.deliveryType === DeliveryEnum.DELIVERY
                      ? 'A domicilio'
                      : 'En tienda'}
                  </span>
                </div>
                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Tienda de entrega
                  </span>
                  <span className="flex-1 sm:flex-3">
                    {shipment.buConsignee?.buCode}-
                    {shipment.buConsignee?.buArea}
                  </span>
                </div>
                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Dirección destino
                  </span>
                  <span className="flex-1 sm:flex-3">
                    {shipment.consigneeAddress?.address}
                  </span>
                </div>
                <div className="flex flex-1 gap-6">
                  <span className="flex flex-1 flex-col text-right font-semibold truncate">
                    Ciudad destino
                  </span>
                  <span className="flex-1 sm:flex-3">
                    {shipment.buConsignee?.location?.name}
                  </span>
                </div>
              </div>

              {/* Details Data */}
              <div className="flex flex-1 flex-col text-gray-800 leading-0 text-xs sm:px-8 mt-6 gap-1 sm:gap-0">
                <div className="flex flex-1 flex-col">
                  <div className="flex flex-1 items-center gap-2">
                    <h3 className="text-md text-main-500 font-bold">
                      Detalles de las piezas
                    </h3>
                  </div>

                  <hr className="border-1.5 border-main-500 mb-3 mt-1" />
                </div>
                <div className="flex flex-1 gap-3 px-12">
                  <table className="table-auto w-full mt-4">
                    <thead className="">
                      <tr>
                        <th className="px-2 py-2">
                          <div className="text-left px-2 py-2 font-semibold text-xs">
                            <span>#</span>
                          </div>
                        </th>
                        <th className="px-2 py-2">
                          <div className="text-left px-4 py-2 font-semibold text-xs">
                            <span>PESO</span>
                          </div>
                        </th>
                        <th className="px-2 py-2">
                          <div className="text-left px-4 py-2 font-semibold text-xs">
                            <span>CATEGORÍA</span>
                          </div>
                        </th>
                        <th className="px-2 py-2">
                          <div className="text-left px-4 py-2 font-semibold text-xs">
                            <span>DIMENSIONES</span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {shipment.pieces?.map((details, index) => {
                        return (
                          <tr
                            key={details?.pieceNumber}
                            className={classNames(
                              'hover:bg-gray-200',
                              index % 2 === 0 && 'bg-gray-100',
                              false && '!bg-blue-200'
                            )}
                          >
                            <td className="px-4 py-2 text-xs text-gray-500 font-semibold">
                              {details?.pieceNumber}
                            </td>
                            <td className="px-6 py-2 text-xs text-gray-500 font-semibold">
                              {details.weight} Kg
                            </td>
                            <td className="px-6 py-2 text-xs text-gray-500 font-semibold">
                              {details.category.name}
                            </td>
                            <td className="px-6 py-2 text-xs text-gray-500 font-semibold">
                              {details.height}cm - {details.length}cm -{' '}
                              {details.width}cm
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* tracking Data */}
              <div className="flex flex-1 flex-col text-gray-800 leading-0 text-xs sm:px-8 mt-6 gap-1 sm:gap-0">
                <div className="flex flex-1 flex-col">
                  <div className="flex flex-1 items-center gap-2">
                    <h3 className="text-md text-main-500 font-bold">
                      Tracking
                    </h3>
                  </div>

                  <hr className="border-1.5 border-main-500 mb-3 mt-1" />
                </div>
                <div className="flex flex-1 gap-3 px-12">
                  {loadingTracking ? (
                    <div className="flex flex-col items-center justify-center">
                      <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
                      <span className="text-sm text-gray-500 mt-2">
                        Cargando...
                      </span>
                    </div>
                  ) : tracking.length > 0 ? (
                    <div className="timeline-container">
                      {tracking.map((item, index) => (
                        <TimelineItem
                          key={index}
                          status={item.status}
                          description={item.description}
                          date={item.date}
                          city={''}
                          state={''}
                          locationZone={''}
                          isLastItem={index === 0}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center">
                      <span className="text-sm text-gray-500">
                        No contiene tracking asociado
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center py-8">
              <span className="text-sm text-gray-500">
                No hay información de la guía
              </span>
            </div>
          )}

          <hr className="border-gray-300 my-6" />

          {/* Footer */}
          <div className="flex flex-1 justify-between items-center">
            <div className="flex gap-4 items-center">
              <Dropdown
                label="Imprimir"
                items={[
                  {
                    items: [
                      {
                        label: 'Documento Bolsillo',
                        icon: (props) => (
                          <PrinterIcon className={props.className} />
                        ),
                        onClick: () =>
                          setTypePrint(PrintShipmenttEnum.DocumentoBolsillo),
                      },
                      {
                        label: 'Etiqueta PDF 10x10',
                        icon: (props) => (
                          <PrinterIcon className={props.className} />
                        ),
                        onClick: () =>
                          setTypePrint(PrintShipmenttEnum.EtiquetaBomPequenna),
                      },
                      {
                        label: 'Etiqueta PDF Carta',
                        icon: (props) => (
                          <PrinterIcon className={props.className} />
                        ),
                        onClick: () =>
                          setTypePrint(PrintShipmenttEnum.EtiquetaBomGrande),
                      },
                      {
                        label: 'Etiqueta Zebra 10x6',
                        icon: (props) => (
                          <PrinterIcon className={props.className} />
                        ),
                        onClick: () =>
                          setTypePrint(PrintShipmenttEnum.EtiquetaZebra),
                      },
                      {
                        label: 'Etiqueta Zebra 10x10',
                        icon: (props) => (
                          <PrinterIcon className={props.className} />
                        ),
                        onClick: () =>
                          setTypePrint(PrintShipmenttEnum.EtiquetaZebra10x10),
                      },
                    ],
                  },
                ]}
                className=""
                buttonClassName="bg-white focus-visible:outline-main-400 text-main-500 hover:bg-main-50 hover:text-main-600 border-main-500 !ring-0 h-[38px] items-center"
                menuClassName="bottom-full mb-2 origin-bottom-right !mt-0"
              />

              {isModifiable && (
                <div>
                  <LinkText
                    text="Anular"
                    onClick={() => setOpenAnnulation(true)}
                  />
                </div>
              )}
            </div>

            <PrimaryButton onClick={() => setOpen(false)}>
              <span className="w-20">Cerrar</span>
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal
        open={openAnulation}
        setOpen={setOpenAnnulation}
        staticBackdrop={true}
        className="flex flex-1 mx-2 my-1 pt-6 pb-4"
        style={{
          maxWidth: '24rem',
        }}
      >
        <div className="flex flex-col items-center justify-center w-full">
          <div className="flex flex-col items-center justify-center w-full mb-4">
            <ExclamationTriangleIcon className="w-16 h-16 text-main-600" />
          </div>

          <p className="mt-2 text-center text-gray-900 px-4">
            ¿Estás seguro que quieres anular la guía{' '}
            <span className="font-bold">{shipment?.shipmentNumber}</span>?
          </p>

          <div className="w-full px-6 mt-4">
            <label
              htmlFor="annulment-reason"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Motivo de la anulación
            </label>
            <textarea
              id="annulment-reason"
              rows={3}
              maxLength={150}
              className="w-full rounded-md border border-gray-300 shadow-sm focus:border-main-500 focus:ring-main-500 sm:text-sm p-2 resize-none"
              value={annulmentObservation}
              onChange={(e) => setAnnulmentObservation(e.target.value)}
              placeholder="Indique el motivo..."
            />
            <div className="flex justify-end">
              <span className="text-xs text-gray-500 mt-1">
                {annulmentObservation.length}/150
              </span>
            </div>
          </div>

          <div className="mt-6 flex w-full flex-row justify-between gap-4 px-6">
            <SecondaryButton
              className="px-4 flex-1 justify-center"
              onClick={() => {
                setOpenAnnulation(false)
                setAnnulmentObservation(shipment.observations || '')
              }}
            >
              Cancelar
            </SecondaryButton>

            <PrimaryButton
              className="px-4 flex-1 justify-center"
              onClick={handleAnulation}
            >
              Anular
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <ModalLoading
        open={loadingModal}
        title={loadingTitle}
        status={loadingStatus}
        setOpen={setLoadingModal}
        timeOut={3000}
      />
    </>
  )
}
